import { computed, ref, ComputedRef } from "@vue/runtime-core";
import {
  composeBetOption,
  composeHomeAwayOptions as composeHomeAwayMarkets,
  composeMarket
} from "@fresh8/ad-data-composition/src/betData";
import { Option } from "@fresh8/types/src/betData";
import { injectDisplayEvents } from "../composition/displayEvents";
import { useTemplateSize } from "@fresh8/ad-data-composition/src/useTemplateSize";
import { injectBrand } from "@fresh8/ad-data-composition/src/brand";
import {
  enabledInsightSizes,
  enabledParlaySizes
} from "@fresh8/types/src/featureFlags";

export function useInsightData(
  marketId: string
): {
  insightText: ComputedRef<string>;
  firstOption: ComputedRef<Option | undefined>;
  secondOption: ComputedRef<Option | undefined>;
  relevantOption: ComputedRef<Option | undefined>;
} {
  const marketData = composeMarket(marketId);
  const markets = composeHomeAwayMarkets(ref([marketId]));

  const firstOption = computed(() => {
    if (markets.value && markets.value[0].options[0]) {
      return composeBetOption(markets.value[0].options[0]).value;
    }
  });

  const secondOption = computed(() => {
    const market = markets.value?.[0];
    if (market && market.options[market.options.length - 1]) {
      const index = market.options.length - 1;
      return composeBetOption(market.options[index]).value;
    }
  });
  const insightText = computed(
    () =>
      (marketData.value?.marketInsight && marketData.value.marketInsight) || ""
  );

  const insightRelavance = computed(() => {
    const marketInsightSide = marketData.value?.marketInsightSide;
    const marketInsightSentiment = marketData.value?.marketInsightSentiment;
    if (!marketInsightSide || !marketInsightSentiment) return undefined;

    if (marketInsightSentiment === "positive") {
      return marketInsightSide;
    } else {
      // display the opposite side for negative
      // focused insights.
      if (marketInsightSide === "home") {
        return "away";
      }
      return "home";
    }
  });

  const relevantOption = computed(() => {
    if (!insightRelavance.value) return undefined;
    if (firstOption.value?.label === insightRelavance.value) {
      return firstOption.value;
    }
    if (secondOption.value?.label === insightRelavance.value) {
      return secondOption.value;
    }
    return undefined;
  });

  return { insightText, firstOption, secondOption, relevantOption };
}

export function useInsightCardDisplayState(): {
  marketInsightsCardIncluded: ComputedRef<boolean>;
  parlayIncluded: ComputedRef<boolean>;
} {
  const marketInsightsCardIncluded = computed(() => {
    const events = injectDisplayEvents();
    const { size } = useTemplateSize();

    const isEnabledSize = enabledInsightSizes.includes(size.value.name);

    if (!isEnabledSize) return false;

    if (!events) return false;

    return (
      events?.flattendOrderedEvents.value?.findIndex(
        card => card[0] === "insight"
      ) !== -1
    );
  });

  const parlayIncluded = computed(() => {
    const { size } = useTemplateSize();
    const brand = injectBrand();
    const events = injectDisplayEvents();

    const isEnabledSize = enabledParlaySizes.includes(size.value.name);

    return !!(
      isEnabledSize &&
      brand?.adTemplate.value.parlayCard?.useParlayCard &&
      events?.parlayMarkets.value?.length === 3
    );
  });

  return {
    marketInsightsCardIncluded,
    parlayIncluded
  };
}
